import { ChangeDetectionStrategy, Component, forwardRef, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { PageFilterGroupType } from '@common/paged-data/enums/page-filter-group-type.enum';
import { Page } from '@common/paged-data/types/page.type';
import { createEmptyPage } from '@common/paged-data/util/page';
import { ProcessAttemptStatus } from '@portal-core/processes/enums/process-attempt-status.enum';
import { AnyTargetId } from '@portal-core/targets/constants/any-target-id.constant';
import { Target } from '@portal-core/targets/models/target.model';
import { TargetsService } from '@portal-core/targets/services/targets.service';
import { TargetPageFilterConfig } from '@portal-core/targets/util/target-page-filter-config';
import { SelectListBase } from '@portal-core/ui/list/util/select-list-base';
import { SelectionListControl } from '@portal-core/ui/list/util/selection-list-control';
import { PageFilterService } from '@portal-core/ui/page-filters/services/page-filter.service';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'mc-targets-select-list',
  templateUrl: './targets-select-list.component.html',
  styleUrls: ['./targets-select-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  // Provide SelectListBase as TargetsSelectListComponent so that this component can work in mc-autocomplete
  providers: [{ provide: SelectListBase, useExisting: forwardRef(() => TargetsSelectListComponent) }]
})
export class TargetsSelectListComponent extends SelectListBase<Target> implements OnChanges {
  /** The process states to filter by. Defaults to builds in any state. */
  @Input() statuses?: ProcessAttemptStatus[];
  /** The project id that the targets belong to. */
  @Input() projectId: number;
  /** Whether to include the Any target option in the list. */
  @Input() showAnyTargetOption?: boolean = false;

  filterConfig: TargetPageFilterConfig = new TargetPageFilterConfig(['Path', 'TargetPath']);

  listControl: SelectionListControl<Target> = new SelectionListControl<Target>(this.targetsService, filter => {
    let fetch$: Observable<Page<Target>> = of(createEmptyPage<Target>());

    if (typeof this.projectId === 'number' && this.projectId > 0) {
      fetch$ = this.targetsService.getBuildTargetsPageByProjectId$(this.projectId, filter);
    }

    return fetch$.pipe(
      tap(page => {
        if (this.showAnyTargetOption) {
          if (page.Page === 0) {
            page.Items.splice(0, 0, this.targetsService.getItemById(AnyTargetId));
          }
        }
      })
    );
  }, target => {
    return target.Id;
  });

  constructor(private targetsService: TargetsService, private pageFilterService: PageFilterService) {
    super(targetsService);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.statuses) {
      this.applyFilter$('targets-select-list-statuses', this.pageFilterService.create({
        Id: 'targets-select-list-statuses',
        Type: PageFilterGroupType.Select
      }).select('Process.LastProcessAttempt.Status', this.statuses).value);
    }

    if (changes.projectId || changes.showAnyTargetOption) {
      this.hardReload();
    }
  }
}
